import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const Blocks = () => {
  const { checkPermission } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fields: [],
    isActive: true
  });

  const inputTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Select Dropdown' },
    { value: 'radio', label: 'Radio Button' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'file', label: 'File Upload' }
  ];

  useEffect(() => {
    fetchBlocks();
  }, []);

  const fetchBlocks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/blocks');
      if (response.data.success) {
        setBlocks(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching blocks:', error);
      toast.error('Failed to fetch blocks');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (block = null) => {
    if (block) {
      setEditingBlock(block);
      setFormData({
        name: block.name,
        description: block.description || '',
        fields: block.fields.map(field => ({ ...field })),
        isActive: block.isActive
      });
    } else {
      setEditingBlock(null);
      setFormData({
        name: '',
        description: '',
        fields: [],
        isActive: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBlock(null);
    setFormData({
      name: '',
      description: '',
      fields: [],
      isActive: true
    });
  };

  const handleAddField = () => {
    setFormData(prev => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          fieldName: '',
          inputType: 'text',
          placeholder: '',
          required: false,
          options: [],
          order: prev.fields.length
        }
      ]
    }));
  };

  const handleRemoveField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleFieldChange = (index, key, value) => {
    setFormData(prev => {
      const newFields = [...prev.fields];
      newFields[index] = {
        ...newFields[index],
        [key]: value
      };
      return {
        ...prev,
        fields: newFields
      };
    });
  };

  const handleOptionsChange = (index, optionsString) => {
    // Store the raw string temporarily, don't process it immediately
    handleFieldChange(index, 'optionsString', optionsString);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Block name is required');
      return;
    }

    // Process optionsString into options array before submitting
    const processedFields = formData.fields.map(field => {
      const processedField = { ...field };
      
      // If field has optionsString, convert it to options array
      if (processedField.optionsString !== undefined) {
        processedField.options = processedField.optionsString
          .split(',')
          .map(opt => opt.trim())
          .filter(opt => opt);
        delete processedField.optionsString;
      }
      
      return processedField;
    });

    const dataToSubmit = {
      ...formData,
      fields: processedFields
    };

    try {
      if (editingBlock) {
        const response = await api.put(`/blocks/${editingBlock._id}`, dataToSubmit);
        if (response.data.success) {
          toast.success('Block updated successfully');
          fetchBlocks();
          handleCloseModal();
        }
      } else {
        const response = await api.post('/blocks', dataToSubmit);
        if (response.data.success) {
          toast.success('Block created successfully');
          fetchBlocks();
          handleCloseModal();
        }
      }
    } catch (error) {
      console.error('Error saving block:', error);
      toast.error(error.response?.data?.message || 'Failed to save block');
    }
  };

  const handleDelete = async (blockId) => {
    if (!window.confirm('Are you sure you want to delete this block?')) {
      return;
    }

    try {
      const response = await api.delete(`/blocks/${blockId}`);
      if (response.data.success) {
        toast.success('Block deleted successfully');
        fetchBlocks();
      }
    } catch (error) {
      console.error('Error deleting block:', error);
      toast.error(error.response?.data?.message || 'Failed to delete block');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blocks</h1>
          <p className="text-gray-600 mt-1">Manage reusable form blocks with custom fields</p>
        </div>
        {checkPermission('Create Block') && (
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Block
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading blocks...</p>
        </div>
      ) : blocks.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No blocks found. Create your first block to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blocks.map((block) => (
            <div key={block._id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{block.name}</h3>
                    {block.description && (
                      <p className="text-sm text-gray-600 mt-1">{block.description}</p>
                    )}
                  </div>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    block.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {block.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">
                    Fields: <span className="font-medium text-gray-900">{block.fields.length}</span>
                  </p>
                  {block.fields.length > 0 && (
                    <div className="space-y-1">
                      {block.fields.slice(0, 3).map((field, idx) => (
                        <div key={idx} className="text-xs text-gray-600 flex items-center">
                          <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                          {field.fieldName} ({field.inputType})
                        </div>
                      ))}
                      {block.fields.length > 3 && (
                        <p className="text-xs text-gray-500 ml-4">+{block.fields.length - 3} more</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
                  {checkPermission('Update Block') && (
                    <button
                      onClick={() => handleOpenModal(block)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                  )}
                  {checkPermission('Delete Block') && (
                    <button
                      onClick={() => handleDelete(block._id)}
                      className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingBlock ? 'Edit Block' : 'Create New Block'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Block Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Block Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter block name"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows="2"
                    placeholder="Enter block description"
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>

                {/* Fields */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Fields
                    </label>
                    <button
                      type="button"
                      onClick={handleAddField}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Field
                    </button>
                  </div>

                  {formData.fields.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-md">
                      <p className="text-gray-500 text-sm">No fields added yet. Click "Add Field" to create one.</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {formData.fields.map((field, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-md bg-gray-50">
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-sm font-medium text-gray-700">Field {index + 1}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveField(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            {/* Field Name */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Field Name *
                              </label>
                              <input
                                type="text"
                                required
                                value={field.fieldName}
                                onChange={(e) => handleFieldChange(index, 'fieldName', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., firstName"
                              />
                            </div>

                            {/* Input Type */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Input Type *
                              </label>
                              <select
                                value={field.inputType}
                                onChange={(e) => handleFieldChange(index, 'inputType', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              >
                                {inputTypes.map(type => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Placeholder */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Placeholder
                              </label>
                              <input
                                type="text"
                                value={field.placeholder}
                                onChange={(e) => handleFieldChange(index, 'placeholder', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter placeholder text"
                              />
                            </div>

                            {/* Required */}
                            <div className="flex items-center pt-6">
                              <input
                                type="checkbox"
                                id={`required-${index}`}
                                checked={field.required}
                                onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`required-${index}`} className="ml-2 block text-xs text-gray-700">
                                Required Field
                              </label>
                            </div>

                            {/* Options (for select, radio, checkbox) */}
                            {['select', 'radio', 'checkbox'].includes(field.inputType) && (
                              <div className="col-span-2">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Options (comma-separated)
                                </label>
                                <input
                                  type="text"
                                  value={field.optionsString !== undefined ? field.optionsString : (field.options?.join(', ') || '')}
                                  onChange={(e) => handleOptionsChange(index, e.target.value)}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="e.g., Approved, Rejected, Pending"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {editingBlock ? 'Update Block' : 'Create Block'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blocks;

